import { z } from 'zod';

export const AppUrlsSchema = z.object({
  teacher: z.url().min(1),
  admin: z.url().min(1),
  student: z.url().min(1),
  search: z.url().min(1),
  site: z.url().min(1),
});

export type AppUrls = z.infer<typeof AppUrlsSchema>;

export type AgreementToBeSignedUrlArgs = {
  agreementId: string;
  secret: string;
  proposalId: string;
};

export type SignedAgreementUrlArgs = {
  agreementId: string;
  secret: string;
  document: string;
};

export type EmailVerificationUrlArgs = {
  token: string;
  language: string;
};

export type DirectLoginUrlArgs = {
  token: string;
  language: string;
};

export type LessonModuleDirectLinkUrlArgs = {
  groupId: string;
  lessonModuleId: string;
};

export class AppUrlProvider {
  public constructor(private readonly appUrls: AppUrls) {}

  public get teacherUrl(): URL {
    return new URL(this.appUrls.teacher);
  }

  public get adminUrl(): URL {
    return new URL(this.appUrls.admin);
  }

  public get studentUrl(): URL {
    return new URL(this.appUrls.student);
  }

  public get searchUrl(): URL {
    return new URL(this.appUrls.search);
  }

  public get siteUrl(): URL {
    return new URL(this.appUrls.site);
  }

  public agreementToBeSignedUrl({ agreementId, secret, proposalId }: AgreementToBeSignedUrlArgs): URL {
    return new URL(`${this.teacherUrl.toString()}app/agreement/GDPR/${agreementId}/${secret}/${proposalId}`);
  }

  public signedAgreementUrl({ agreementId, secret, document }: SignedAgreementUrlArgs): URL {
    return new URL(`${this.teacherUrl.toString()}app/agreement-documents/${document}/${agreementId}/${secret}`);
  }

  public keepAccountUrl(): URL {
    return new URL(`${this.teacherUrl.toString()}app/account/profile`);
  }

  public emailVerificationUrl({ token, language }: EmailVerificationUrlArgs): URL {
    return new URL(`${this.searchUrl.toString()}${language}/register/verify-email?token=${token}`);
  }

  public directLoginUrl({ token, language }: DirectLoginUrlArgs): URL {
    return new URL(`${this.teacherUrl.toString()}app/directLogin?token=${token}&language=${language}`);
  }

  public forgotPasswordUrl(language: string): URL {
    return new URL(`${this.searchUrl.toString()}${language}/forgot-password`);
  }

  public newMagicLinkUrl(language: string): URL {
    return new URL(`${this.searchUrl.toString()}${language}/magicLink`);
  }

  public lessonModuleDirectLinkUrl({ groupId, lessonModuleId }: LessonModuleDirectLinkUrlArgs): URL {
    return new URL(`${this.studentUrl.toString()}modules/${groupId}/${lessonModuleId}`);
  }

  public teacherGroupDirectLinkUrl(groupId: string): URL {
    return new URL(`${this.teacherUrl.toString()}app/explorer/groups/${groupId}`);
  }
}
